import {
  CheckoutEventNames,
  type CurrencyCode,
  type Environments,
  initializePaddle,
  type Paddle,
  type PaddleEventData,
  type PricePreviewResponse,
} from '@paddle/paddle-js'
import type { CheckoutCustomerAddress } from '@paddle/paddle-js/types/checkout/customer'
import { isString } from 'fitify-types/src/type-guards/common'
import type { UserLocation } from 'fitify-ui/src/server/types/user-location'
import { getNavigatorLocale } from 'fitify-ui/src/utils/language'

import {
  PlanType,
  type SubscriptionPlan,
  type SubscriptionPlanSchema,
} from '../types/subscription'

import { getCurrencyDenominator } from './plan-price'
import { logSubscribeEvent } from './subscription-events'

export const PADDLE_CHECKOUT_ELEMENT_ID = 'paddle-checkout'
export const PADDLE_CHECKOUT_MIN_HEIGHT = 450

export type PaddleCredentials = {
  environment: Environments
  token: string
}

export const initPaddle = async ({
  credentials,
  locale,
  successPath,
}: {
  credentials: PaddleCredentials
  locale?: string
  successPath?: string
}) => {
  return await initializePaddle({
    environment: credentials.environment,
    token: credentials.token,
    pwCustomer: {}, // Always pass in empty object and enhance it when user (Paddle customer) logs in
    checkout: {
      settings: {
        allowDiscountRemoval: false,
        displayMode: 'inline',
        frameTarget: PADDLE_CHECKOUT_ELEMENT_ID,
        frameInitialHeight: PADDLE_CHECKOUT_MIN_HEIGHT,
        frameStyle:
          'width: 100%; min-width: 256px; background-color: transparent; border: none;',
        locale,
        showAddTaxId: false,
        successUrl: successPath,
        variant: 'multi-page',
      },
    },
  })
}

export const handlePaddleEvent = ({
  event,
  onCheckoutCompleted,
  onPaymentInitiated,
  paddle,
  plan,
}: {
  event: PaddleEventData
  onCheckoutCompleted?: (customerEmail?: string) => void
  onPaymentInitiated?: (plan?: SubscriptionPlan) => void
  paddle?: Paddle
  plan?: SubscriptionPlan
}) => {
  if (event.name === CheckoutEventNames.CHECKOUT_PAYMENT_INITIATED) {
    onPaymentInitiated?.(plan)
  }
  if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
    // Trial state comes from the checkout event rather than the plan, since
    // Paddle is authoritative about what was actually purchased.
    logSubscribeEvent({
      currency: event.data?.currency_code,
      intervalLength: plan?.intervalLength,
      intervalUnit: plan?.intervalUnit,
      isTrial: Boolean(event.data?.items?.[0].trial_period),
      priceTotalValue: plan?.priceTotalValue,
      sku: plan?.sku,
      totalValue: plan?.totalValue,
      transactionId: event.data?.transaction_id,
      type: plan?.type,
    })

    onCheckoutCompleted?.(event.data?.customer.email)

    if (event.data?.customer.email) {
      paddle?.Update({
        pwCustomer: {
          email: event.data.customer.email,
        },
      })
    }
  }
}

export const isPaddleId = (value: unknown): value is string => {
  const paddleIdRegex = RegExp(/[a-zA-Z]{3}_.*/)
  if (isString(value)) {
    return paddleIdRegex.test(value)
  }
  return false
}

type GetSubscriptionPlanSchemaTypeFieldsArgs = {
  item: PricePreviewResponse['data']['details']['lineItems'][0]
  totalValue: number
  currency: CurrencyCode
  denominator: number
}

const getPriceTotalValueForDiscountedPlan = ({
  item,
  totalValue,
  currency,
  discountAmount,
  denominator,
}: GetSubscriptionPlanSchemaTypeFieldsArgs & { discountAmount: number }) => {
  try {
    if (item.price.unitPrice.currencyCode === currency) {
      return Number(item.price.unitPrice.amount) / denominator
    }
    const unitPriceOverride = item.price.unitPriceOverrides.find(
      (override) => override.unitPrice.currencyCode === currency
    )
    if (unitPriceOverride) {
      return Number(unitPriceOverride.unitPrice.amount) / denominator
    }
  } catch {
    // Ignore error and use fallback
  }
  console.warn(
    'Failed to get price total value for discounted plan, using calculated fallback value'
  )

  return totalValue / ((100 - discountAmount) / 100)
}

const getSubscriptionPlanSchemaTypeFields = ({
  item,
  totalValue,
  currency,
  denominator,
}: GetSubscriptionPlanSchemaTypeFieldsArgs): Partial<SubscriptionPlanSchema> => {
  if (item.price.trialPeriod) {
    return {
      type: PlanType.TRIAL,
      discountDurationLength: item.price.trialPeriod.frequency,
      discountDurationUnit: item.price.trialPeriod.interval,
    }
  }
  if (item.discounts.length > 0) {
    const priceId = item.price.id
    const discount = item.discounts[0].discount
    const isDiscountForProduct =
      !discount.restrictTo ||
      discount.restrictTo.some(
        (restrictToPriceId) => priceId === restrictToPriceId
      )
    if (
      isDiscountForProduct &&
      discount.type === 'percentage' &&
      isString(discount.amount)
    ) {
      const discountAmount = Number(discount.amount)
      return {
        type: PlanType.DISCOUNTED,
        discountDurationLength: discount.maximumRecurringIntervals ?? undefined,
        discountPercentValue: Number(discount.amount),
        discountDurationUnit: item.price.billingCycle?.interval,
        priceTotalValue: getPriceTotalValueForDiscountedPlan({
          currency,
          denominator,
          discountAmount,
          item,
          totalValue,
        }),
      }
    }
  }
  return {
    type: PlanType.REGULAR,
  }
}

export const getSubscriptionPlanSchema = (
  response: PricePreviewResponse,
  item: PricePreviewResponse['data']['details']['lineItems'][0]
): Partial<SubscriptionPlanSchema> => {
  const currency = response.data.currencyCode
  const denominator = getCurrencyDenominator(currency)
  const totalValue = Number(item.totals.total) / denominator
  const plan = {
    intervalUnit: item.price.billingCycle?.interval,
    intervalLength: item.price.billingCycle?.frequency,
    locale: getNavigatorLocale(),
    currency,
    totalValue: totalValue,
    priceTotalValue: totalValue,
    id: item.price.id,
    sku: item.price.description,
  }

  return {
    ...plan,
    ...getSubscriptionPlanSchemaTypeFields({
      item,
      totalValue,
      currency,
      denominator,
    }),
  }
}

export const getPaddleAddress = (
  userLocation: UserLocation
): CheckoutCustomerAddress => {
  return {
    city: userLocation.city,
    countryCode: userLocation.countryCode,
    postalCode: userLocation.postalCode,
    region: userLocation.region,
  }
}
